import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import {
  Wallet,
  Loader2,
  CheckCircle,
  AlertCircle,
  Shield,
  CreditCard,
} from "lucide-react";
import { oxpayAPI } from "@/lib/oxpay";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "react-toastify";

export default function CreateWallet() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [walletStatus, setWalletStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccountData();
  }, [user?.email]);

  const fetchAccountData = async () => {
    if (!user?.email) return;

    try {
      const { data, error } = await supabase
        .from("accounts")
        .select("*")
        .eq("email", user.email)
        .single();

      if (data && !error) {
        setAccountData(data);

        // Check if wallet already exists
        if (data.wallet_id) {
          setWalletStatus("created");
        }
      }
    } catch (error) {
      console.error("Error fetching account data:", error);
    }
  };

  const createWallet = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check prerequisites
      if (!accountData?.oxpay_user_id) {
        setError("Please complete user registration first");
        setTimeout(() => navigate("/register-user"), 2000);
        return;
      }

      if (accountData?.kyc_status !== "approved") {
        setError("Please complete KYC verification first");
        setTimeout(() => navigate("/kyc-onfido"), 2000);
        return;
      }

      // Call OxPay API to create wallet
      const response = await oxpayAPI.createWallet(accountData.oxpay_user_id, {
        nationality: accountData.nationality,
        id_type: accountData.id_type,
        id_number: accountData.id_number,
      });
      console.log("Create wallet response:", response);

      if (response?.headers?.status_code === "success" && response?.body) {
        const walletData = response.body;

        // Update database with wallet information
        const { error: updateError } = await supabase
          .from("accounts")
          .update({
            wallet_id: walletData.id || walletData.wallet_id,
            wallet_status: "active",
            wallet_created_at: new Date().toISOString(),
          })
          .eq("email", user?.email);

        if (updateError) {
          throw updateError;
        }

        setWalletStatus("created");
        toast.success("Wallet created successfully!");

        // Redirect to home after 2 seconds
        setTimeout(() => navigate("/home"), 2000);
      } else {
        throw new Error(
          response?.body?.description || "Failed to create wallet"
        );
      }
    } catch (error) {
      console.error("Create wallet error:", error);
      setError(error.message || "Failed to create wallet. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    createWallet();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/home")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Title */}
        <div className="mt-6 mb-6">
          <h1 className="text-2xl font-bold">Create Your Wallet</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Set up your digital wallet to manage funds and cards
          </p>
        </div>

        {/* Wallet Status Card */}
        {walletStatus === "created" ? (
          <Card className="bg-green-50 dark:bg-green-950/20 border-green-600">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div>
                  <h3 className="font-semibold">
                    Wallet Created Successfully!
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your wallet is ready. Redirecting to home...
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Prerequisites Card */}
            <Card className="bg-card mb-4">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Prerequisites
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 mt-0.5 flex-shrink-0 ${
                      accountData?.oxpay_user_id
                        ? "bg-green-600 border-green-600"
                        : "border-muted-foreground"
                    }`}
                  >
                    {accountData?.oxpay_user_id && (
                      <svg
                        className="w-3 h-3 text-white m-auto"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-sm">User Registration</p>
                    <p className="text-xs text-muted-foreground">
                      Complete your user profile registration
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 mt-0.5 flex-shrink-0 ${
                      accountData?.kyc_status === "approved"
                        ? "bg-green-600 border-green-600"
                        : "border-muted-foreground"
                    }`}
                  >
                    {accountData?.kyc_status === "approved" && (
                      <svg
                        className="w-3 h-3 text-white m-auto"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-sm">KYC Verification</p>
                    <p className="text-xs text-muted-foreground">
                      Complete identity verification process
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Features Card */}
            <Card className="bg-card mb-4">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Wallet Features
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-4 h-4 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">
                      Virtual & Physical Cards
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Create and manage multiple cards
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="w-4 h-4 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Secure Transactions</p>
                    <p className="text-xs text-muted-foreground">
                      Bank-grade security for all payments
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Wallet className="w-4 h-4 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Fund Management</p>
                    <p className="text-xs text-muted-foreground">
                      Top up and withdraw funds easily
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Security Notice */}
            <Card className="bg-muted/50 mb-6">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <div className="text-xs">
                    <p className="font-medium mb-1">Important Notice</p>
                    <p className="text-muted-foreground">
                      Your wallet will be created with bank-grade security. All
                      transactions are encrypted and monitored for fraud
                      prevention.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Error Alert */}
            {error && (
              <Alert className="border-red-600 bg-red-50 dark:bg-red-950/20 mb-4">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800 dark:text-red-200">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {error ? (
                <div className="flex gap-2">
                  <Button
                    onClick={handleRetry}
                    className="flex-1"
                    disabled={isLoading}
                  >
                    Try Again
                  </Button>
                  <Button
                    onClick={() => navigate("/home")}
                    className="flex-1"
                    variant="outline"
                  >
                    Go Back
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={createWallet}
                  disabled={
                    isLoading ||
                    !accountData?.oxpay_user_id ||
                    accountData?.kyc_status !== "approved" ||
                    walletStatus === "created"
                  }
                  className="w-full h-12"
                  size="lg"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Wallet...
                    </>
                  ) : walletStatus === "created" ? (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Wallet Created
                    </>
                  ) : (
                    <>
                      <Wallet className="w-4 h-4 mr-2" />
                      Create Wallet
                    </>
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <BottomBar />
    </div>
  );
}
